/**
 * OAuth session -> Agent (web).
 *
 * Wraps an atproto `OAuthSession` in a standard `Agent` so the rest of the app
 * (and the video-call service-auth path) treats it like any other session.
 * `dispatchUrl` is set to the token's PDS (aud) so `agent.dispatchUrl`-based
 * helpers (e.g. getServiceAuthToken) resolve the correct audience.
 *
 * Ported from eurosky-social-app, adapted to bateclub's SessionAccount shape.
 * Web-only (OAuthSession comes from @atproto/oauth-client-browser).
 */

import {
  Agent,
  type AtpSessionData,
  type ComAtprotoServerGetSession,
} from '@atproto/api'
import {type OAuthSession} from '@atproto/oauth-client-browser'

import {BLUESKY_PROXY_HEADER, BSKY_SERVICE} from '#/lib/constants'
import {logger} from '#/logger'
import {sessionAccountToSession} from './agent'
import {configureModerationForAccount} from './moderation'
import {oauthRestore} from './oauth-web-client'
import {type SessionAccount} from './types'

const OAUTH_TIMEOUT_MS = 10_000

function withTimeout<T>(p: Promise<T>, label: string): Promise<T> {
  return Promise.race([
    p,
    new Promise<never>((_, reject) =>
      setTimeout(
        () => reject(new Error(`${label} timed out`)),
        OAUTH_TIMEOUT_MS,
      ),
    ),
  ])
}

export async function oauthCreateAgent(session: OAuthSession) {
  const agent = new OauthBskyAppAgent(session)
  const account = await oauthAgentAndSessionToSessionAccountOrThrow(
    agent,
    session,
  )
  // configureModerationForAccount only reads agent identity for a test-only
  // global; the OAuth Agent is API-compatible for that use.
  const moderation = configureModerationForAccount(agent as never, account)
  return agent.prepare(account, [moderation])
}

export async function oauthResumeSession(account: SessionAccount) {
  const session = await withTimeout(
    oauthRestore(account.did),
    'OAuth session restore',
  )
  return oauthCreateAgent(session)
}

async function oauthAgentAndSessionToSessionAccountOrThrow(
  agent: Agent,
  session: OAuthSession,
): Promise<SessionAccount> {
  const account = await oauthAgentAndSessionToSessionAccount(agent, session)
  if (!account) {
    throw new Error('Expected an active OAuth session')
  }
  return account
}

async function oauthAgentAndSessionToSessionAccount(
  agent: Agent,
  session: OAuthSession,
): Promise<SessionAccount | undefined> {
  let data: ComAtprotoServerGetSession.OutputSchema
  try {
    const res = await withTimeout(
      agent.com.atproto.server.getSession(),
      'getSession',
    )
    data = res.data
  } catch (e) {
    logger.error('oauth: getSession failed', {
      safeMessage: e instanceof Error ? e.message : String(e),
    })
    return undefined
  }

  let aud: string
  try {
    const tokenInfo = await withTimeout(
      session.getTokenInfo(false),
      'getTokenInfo',
    )
    aud = tokenInfo.aud
  } catch (e) {
    logger.error('oauth: getTokenInfo failed', {
      safeMessage: e instanceof Error ? e.message : String(e),
    })
    return undefined
  }

  return {
    service: session.serverMetadata.issuer,
    did: session.did,
    handle: data.handle,
    email: data.email,
    emailConfirmed: data.emailConfirmed,
    emailAuthFactor: data.emailAuthFactor,
    active: data.active,
    status: data.status,
    pdsUrl: aud,
    isSelfHosted: !session.serverMetadata.issuer.startsWith(BSKY_SERVICE),
    isOauthSession: true,
  }
}

export class OauthBskyAppAgent extends Agent {
  session?: AtpSessionData
  dispatchUrl?: string

  constructor(session: OAuthSession) {
    super(session)
  }

  async prepare(account: SessionAccount, resolvers: Promise<unknown>[]) {
    this.session = sessionAccountToSession(account)
    this.dispatchUrl = account.pdsUrl
    this.configureProxy(BLUESKY_PROXY_HEADER.get())
    await Promise.all(resolvers)
    return {account, agent: this}
  }

  dispose() {}
}
